import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';

import {
  AIVisibilityResponse,
  AIVisibilityChartData,
  AIVisibilityContext,
  AIVisibilityDataPoint
} from '../../../services/brand-index.service';
import { CitationBadgeComponent } from '../citation-badge/citation-badge.component';
import { CommonSourcesComponent } from '../template-ai-common/common-sources/common-sources.component';
import { CompositeScoreWidgetComponent } from '../composite-score-widget/composite-score-widget.component';

@Component({
  selector: 'app-ai-visibility',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatExpansionModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    NgxChartsModule,
    CommonSourcesComponent,
    BarLineComboChartComponent,
    CompositeScoreWidgetComponent
    // CitationBadgeComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ai-visibility.component.html',
  styleUrl: './ai-visibility.component.scss'


})
export class AIVisibilityComponent implements OnInit, OnChanges {
  @Input() aiVisibilityResponse!: AIVisibilityResponse;

  // Chart data properties
  barChartData: any[] = [];
  lineChartData: any[] = [];

  // Composite Score Data - Dynamic from API
  compositeScore: number = 0;

  // Color schemes for charts - different colors for each bar
  colorScheme: any = {
    domain: ['#1976d2', '#ff9800', '#4caf50', '#f44336', '#9c27b0', '#00bcd4', '#795548', '#607d8b']
  };

  // Combo Chart Color Schemes - matching ngx-charts structure
  comboBarScheme: any = {
    name: 'singleLightBlue',
    selectable: true,
    group: 'ordinal',
    domain: ['#01579b']
  };

  lineChartScheme: any = {
    name: 'coolthree',
    selectable: true,
    group: 'ordinal',
    domain: ['#01579b', '#7aa3e5', '#a8385d', '#00bfa5']
  };

  // Responsive view dimensions
  view: [number, number] = [600, 400];

  // Cached processed data to avoid recalculation
  private _cachedBarData: any[] = [];
  private _cachedLineData: any[] = [];
  private _cachedFirstUniversityName: string = '';

  // Combined chart data for proper ngx-charts combo chart
  private _combinedChartData: any[] = [];
  private _combinedLineData: any[] = [];

  // Unified chart data - single array with both bar and line series
  private _unifiedChartData: any[] = [];

  // Getter properties for template optimization
  get processedBarData(): any[] {
    return this.getProcessedBarData();
  }

  get processedLineData(): any[] {
    return this.getProcessedLineData();
  }

  get firstUniversityName(): string {
    return this.getFirstUniversityName();
  }

  // Combined chart data getters for proper ngx-charts combo chart
  get combinedChartData(): any[] {
    return this._combinedChartData;
  }

  get combinedLineData(): any[] {
    return this._combinedLineData;
  }

  // Unified chart data getter
  get unifiedChartData(): any[] {
    return this._unifiedChartData;
  }

  constructor(private cdr: ChangeDetectorRef) {}

  // Combo Chart Axis Scaling and Formatting - matching ngx-charts structure
  yLeftAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yRightAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yLeftTickFormat(data: number): string {
    return `${data.toLocaleString()}`;
  }

  yRightTickFormat(data: number): string {
    return `${data}%`;
  }

  onSelect(event: any): void {
    // console.log('Chart item selected:', event);
  }

  // Combo chart event handlers
  onClick(event: any): void {
    // console.log('Combo chart clicked:', event);
  }

  onActivate(event: any): void {
    // console.log('Combo chart activated:', event);
  }

  onDeactivate(event: any): void {
    // console.log('Combo chart deactivated:', event);
  }

  // Chart dimension and scale methods
  updateXAxisHeight(height: number): void {
    // Update x-axis height
  }

  updateYAxisWidth(width: number): void {
    // Update y-axis width
  }

  // Axis tick formatting methods
  xAxisTickFormatting(value: any): string {
    return value;
  }

  yAxisTickFormatting(value: any): string {
    return value.toString();
  }

  // Legend position for combo chart
  legendPosition: any = 'right';

  // Combo chart properties
  width = 600;
  height = 400;
  legendSpacing = 150;
  legend = true;
  legendOptions: any = {};
  activeEntries: any[] = [];
  animations = true;
  xAxis = true;
  yAxis = true;
  showXAxisLabel = true;
  showYAxisLabel = true;
  xAxisLabel = 'AI Visibility Metrics';
  yAxisLabel = 'Score';
  showGridLines = true;
  trimXAxisTicks = true;
  trimYAxisTicks = true;
  rotateXAxisTicks = true;
  maxXAxisTickLength = 16;
  maxYAxisTickLength = 16;
  roundEdges = true;
  gradient = false;
  tooltipDisabled = false;

  // Chart data properties
  results: any[] = [];
  lineChart: any[] = [];
  colors: any;
  transform: string = '';
  dims: any;
  xScale: any;
  yScale: any;

  ngOnInit() {
    // console.log('🔄 AI Visibility: Component initialized');
    // console.log('🔄 AI Visibility: Initial data:', this.aiVisibilityResponse);

    this.updateChartData();
    this.updateCompositeScoreData();
    this.updateViewByBreakpoint();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateViewByBreakpoint, { passive: true });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['aiVisibilityResponse']) {
      // console.log('AI Visibility: Data changed, updating charts...');
      this.clearCache();
      this.updateChartData();
      this.updateCompositeScoreData();
      this.cdr.markForCheck();
    }
  }

  // Public method to refresh data when API call completes
  public refreshData(newData: AIVisibilityResponse) {
    // console.log('🔄 AI Visibility: Refreshing with new data:', newData);
    // console.log('📋 AI Visibility: Complete API response structure:', JSON.stringify(newData, null, 2));

    if (newData && newData.universities) {
      // console.log('🏫 AI Visibility: Universities data:', newData.universities);
      // console.log('🔢 AI Visibility: Total universities count:', newData.universities.length);
      newData.universities.forEach((uni, index) => {
        // console.log(`🏫 AI Visibility: University ${index + 1} data:`, uni);
        // console.log(`🔑 AI Visibility: University ${index + 1} available properties:`, Object.keys(uni));
      });
    } else {
      // console.log('❌ AI Visibility: Invalid or empty data received');
    }

    this.aiVisibilityResponse = newData;
    this.clearCache();
    this.updateChartData();
    this.updateCompositeScoreData();

    // Debug current state
    this.debugCurrentState();

    this.cdr.markForCheck();
  }

  // Public method to check if component has data
  public hasData(): boolean {
    return !!(this.aiVisibilityResponse && this.aiVisibilityResponse.universities && this.aiVisibilityResponse.universities.length > 0);
  }

  // Public method to get current data status
  public getDataStatus(): string {
    if (!this.aiVisibilityResponse) return 'No data';
    if (!this.aiVisibilityResponse.universities) return 'No universities data';
    if (this.aiVisibilityResponse.universities.length === 0) return 'Empty universities array';
    return `Ready with ${this.aiVisibilityResponse.universities.length} universities`;
  }

  // Debug method to check current component state
  private debugCurrentState(): void {
    // console.log('🔍 AI Visibility: CURRENT STATE DEBUG');
    // console.log('📊 shouldShowBarChart():', this.shouldShowBarChart());
    // console.log('📈 shouldShowComboChart():', this.shouldShowComboChart());
    // console.log('📋 barChartData length:', this.barChartData.length);
    // console.log('📈 lineChartData length:', this.lineChartData.length);
    // console.log('🏫 Universities count:', this.aiVisibilityResponse?.universities?.length || 0);
  }

  // Determine if we should show bar chart only (single university)
  shouldShowBarChart(): boolean {
    // Always return false - we'll use combo-chart-v2 for both single and multiple universities
    // console.log('AI Visibility: shouldShowBarChart() - Always returning false, using combo-chart-v2 for all cases');
    return false;
  }

  // Check if we should show only the "you" university (for single analysis mode)
  private shouldShowOnlyYouUniversity(): boolean {
    // Return false to show combo chart for multiple universities
    // Return true only for true single university analysis
    return false;
  }

  // Determine if we should show combo chart (now used for both single and multiple universities)
  shouldShowComboChart(): boolean {
    if (!this.aiVisibilityResponse?.universities) {
      // console.log('AI Visibility: shouldShowComboChart() - No universities data, returning false');
      return false;
    }

    const universities = this.aiVisibilityResponse.universities;
    const shouldShow = universities.length > 0; // Show combo chart for any number of universities
    // console.log('🎯 AI Visibility: shouldShowComboChart() - Universities count:', universities.length, 'Should show combo chart:', shouldShow);

    return shouldShow;
  }

  // Get appropriate chart subtitle
  getChartSubtitle(): string {
    if (!this.aiVisibilityResponse?.universities) return 'AI Visibility scores comparison';

    const universities = this.aiVisibilityResponse.universities;
    if (universities.length === 1) {
      const university = universities[0];
      return `📊 Single University Analysis: ${university.name} - Detailed AI Visibility metrics breakdown`;
    } else {
      const youUniversity = this.findYouUniversity(universities);
      const competitorCount = universities.length - 1;
      if (youUniversity) {
        return `AI Visibility comparison: ${youUniversity.name} (bars) vs ${competitorCount} competitor${competitorCount > 1 ? 's' : ''} (lines)`;
      } else {
        return `AI Visibility comparison across ${universities.length} universities`;
      }
    }
  }

  // Update responsive view dimensions
  private updateViewByBreakpoint = () => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    if (width <= 480) {
      this.view = [300, 300];
    } else if (width <= 768) {
      this.view = [400, 350];
    } else if (width <= 1024) {
      this.view = [500, 400];
    } else {
      this.view = [600, 400];
    }
    this.cdr.markForCheck();
  }

  // Update chart data based on current response
  private updateChartData() {
    // console.log('🔄 AI Visibility: updateChartData() called');
    // console.log('🔄 AI Visibility: aiVisibilityResponse:', this.aiVisibilityResponse);

    if (!this.aiVisibilityResponse) {
      // console.log('❌ AI Visibility: No aiVisibilityResponse available');
      this.barChartData = [];
      this.lineChartData = [];
      return;
    }

    if (!this.aiVisibilityResponse.universities) {
      // console.log('❌ AI Visibility: No universities data available');
      this.barChartData = [];
      this.lineChartData = [];
      return;
    }

    const universities = this.aiVisibilityResponse.universities;
    // console.log('✅ AI Visibility: Processing universities:', universities);
    // console.log('✅ AI Visibility: Universities count:', universities.length);

    if (this.shouldShowBarChart()) {
      // Single university - show bar chart with detailed metrics
      // console.log('🎯 AI Visibility: SINGLE UNIVERSITY ANALYSIS MODE');
      // console.log('📊 AI Visibility: Showing bar chart only for single university:', universities[0].name);
      // console.log('📈 AI Visibility: Universities count:', universities.length);
      // console.log('🔍 AI Visibility: University data:', universities[0]);

      this.barChartData = this.prepareBarChartData(universities[0]);
      this.lineChartData = []; // FORCE clear line data for single university
      // console.log('✅ AI Visibility: Bar chart data prepared:', this.barChartData);
      // console.log('🚫 AI Visibility: Line chart data cleared for single university:', this.lineChartData);
    } else if (this.shouldShowComboChart()) {
      // Multiple universities - show combo chart (bars for "you" + lines for competitors)
      const youUniversity = this.findYouUniversity(universities);
      const competitorUniversities = this.findCompetitorUniversities(universities);

      // console.log('🎯 AI Visibility: Showing UNIFIED combo chart');
      // console.log('📊 AI Visibility: You university:', youUniversity?.name);
      // console.log('📈 AI Visibility: Competitor universities:', competitorUniversities.map(u => u.name));

      // Use the new unified chart data preparation method
      this.prepareUnifiedChartData(youUniversity, competitorUniversities);

      // Keep legacy data for backward compatibility
      this.prepareCombinedChartData(youUniversity, competitorUniversities);

      // Prepare combo chart data
      this.prepareComboChartData(youUniversity, competitorUniversities);

      if (youUniversity) {
        this.barChartData = this.prepareBarChartData(youUniversity);
      } else {
        this.barChartData = [];
      }

      if (competitorUniversities.length > 0) {
        this.lineChartData = this.prepareLineChartData(competitorUniversities);
      } else {
        this.lineChartData = [];
      }
    }

    // console.log('AI Visibility: Final bar chart data:', this.barChartData);
    // console.log('AI Visibility: Final line chart data:', this.lineChartData);
  }

  // Update composite score data dynamically from API response
  private updateCompositeScoreData() {
    if (!this.aiVisibilityResponse?.universities || this.aiVisibilityResponse.universities.length === 0) {
      this.compositeScore = 0;
      return;
    }

    // For single university, use that university's data
    // For multiple universities, use the "you" university or first university
    let targetUniversity = this.aiVisibilityResponse.universities[0];

    if (this.aiVisibilityResponse.universities.length > 1) {
      const youUniversity = this.findYouUniversity(this.aiVisibilityResponse.universities);
      if (youUniversity) {
        targetUniversity = youUniversity;
      }
    }

    // Calculate composite score as average of all metrics
    const metrics = [
      targetUniversity.ai_visibility_score || 0,
      targetUniversity.content_quality || 0,
      targetUniversity.digital_presence || 0,
      targetUniversity.innovation_index || 0,
      targetUniversity.ai_adoption_rate || 0
    ];

    this.compositeScore = Math.round(metrics.reduce((sum, score) => sum + score, 0) / metrics.length);

    // console.log('🔄 AI Visibility: Updated composite score:', this.compositeScore);
  }

  // Prepare bar chart data for single university - using dynamic data from API
  private prepareBarChartData(university: any): any[] {
    // console.log('🎯 AI Visibility: Preparing SINGLE UNIVERSITY bar chart data');
    // console.log('📋 AI Visibility: University object:', university);
    // console.log('🔑 AI Visibility: Available properties:', Object.keys(university));

    // Use the actual data structure from the API response
    const chartData = [];

    // Add all available metrics dynamically
    if (university.ai_visibility_score !== undefined) {
      chartData.push({ name: 'AI Visibility Score', value: university.ai_visibility_score });
    }
    if (university.content_quality !== undefined) {
      chartData.push({ name: 'Content Quality', value: university.content_quality });
    }
    if (university.digital_presence !== undefined) {
      chartData.push({ name: 'Digital Presence', value: university.digital_presence });
    }
    if (university.innovation_index !== undefined) {
      chartData.push({ name: 'Innovation Index', value: university.innovation_index });
    }
    if (university.ai_adoption_rate !== undefined) {
      chartData.push({ name: 'AI Adoption Rate', value: university.ai_adoption_rate });
    }

    // Add any other metrics that might be in the API response
    Object.keys(university).forEach(key => {
      if (typeof university[key] === 'number' &&
          !['ai_visibility_score', 'content_quality', 'digital_presence', 'innovation_index', 'ai_adoption_rate'].includes(key)) {
        const metricName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        chartData.push({ name: metricName, value: university[key] });
      }
    });

    // console.log('AI Visibility: Generated bar chart data:', chartData);
    return chartData;
  }

  // Prepare line chart data for multiple universities - using dynamic data from API
  private prepareLineChartData(universities: any[]): any[] {
    // console.log('AI Visibility: Preparing line chart data for universities:', universities);

    // For combo chart, line chart should show each competitor university as a separate line
    // Each line represents all metrics for that university
    return universities.map(university => ({
      name: university.name,
      series: [
        { name: 'AI Visibility Score', value: university.ai_visibility_score || 0 },
        { name: 'Content Quality', value: university.content_quality || 0 },
        { name: 'Digital Presence', value: university.digital_presence || 0 },
        { name: 'Innovation Index', value: university.innovation_index || 0 },
        { name: 'AI Adoption Rate', value: university.ai_adoption_rate || 0 }
      ]
    }));
  }

  // Prepare combined chart data for proper ngx-charts combo chart structure
  private prepareCombinedChartData(youUniversity: any, competitorUniversities: any[]): void {
    // console.log('🎯 AI Visibility: Preparing combined chart data for ngx-charts combo chart');
    // console.log('📊 AI Visibility: You university:', youUniversity?.name);
    // console.log('📈 AI Visibility: Competitor universities:', competitorUniversities.map(u => u.name));

    // Prepare bar chart data (for "YOU" university)
    if (youUniversity) {
      this._combinedChartData = this.prepareBarChartData(youUniversity);
    } else {
      this._combinedChartData = [];
    }

    // Prepare line chart data (for competitor universities) - each competitor as a separate line
    if (competitorUniversities.length > 0) {
      this._combinedLineData = competitorUniversities.map(university => ({
        name: university.name,
        series: [
          { name: 'AI Visibility Score', value: university.ai_visibility_score || 0 },
          { name: 'Content Quality', value: university.content_quality || 0 },
          { name: 'Digital Presence', value: university.digital_presence || 0 },
          { name: 'Innovation Index', value: university.innovation_index || 0 },
          { name: 'AI Adoption Rate', value: university.ai_adoption_rate || 0 }
        ]
      }));
    } else {
      this._combinedLineData = [];
    }

    // console.log('✅ AI Visibility: Combined chart data prepared:', {
    //   barData: this._combinedChartData,
    //   lineData: this._combinedLineData
    // });
  }

  // Prepare unified chart data - single array with both bar and line series
  private prepareUnifiedChartData(youUniversity: any, competitorUniversities: any[]): void {
    // console.log('🎯 AI Visibility: Preparing unified chart data');
    // console.log('📊 AI Visibility: You university:', youUniversity?.name);
    // console.log('📈 AI Visibility: Competitor universities:', competitorUniversities.map(u => u.name));

    // Create a single array that combines both bar and line data
    const unifiedData: any[] = [];

    // Add "YOU" university as bar series - each metric as a separate bar
    if (youUniversity) {
      const metrics = [
        { name: 'AI Visibility Score', value: youUniversity.ai_visibility_score || 0 },
        { name: 'Content Quality', value: youUniversity.content_quality || 0 },
        { name: 'Digital Presence', value: youUniversity.digital_presence || 0 },
        { name: 'Innovation Index', value: youUniversity.innovation_index || 0 },
        { name: 'AI Adoption Rate', value: youUniversity.ai_adoption_rate || 0 }
      ];

      metrics.forEach(metric => {
        unifiedData.push({
          name: metric.name,
          value: metric.value,
          series: youUniversity.name, // Bar series for "YOU" university
          university: youUniversity.name
        });
      });
    }

    this._unifiedChartData = unifiedData;

    // console.log('✅ AI Visibility: Unified chart data prepared:', {
    //   totalItems: this._unifiedChartData.length,
    //   youUniversity: youUniversity?.name,
    //   competitorCount: competitorUniversities.length
    // });
  }

  // Prepare combo chart data for ngx-charts-combo-chart
  private prepareComboChartData(youUniversity: any, competitorUniversities: any[]): void {
    // console.log('🎯 AI Visibility: Preparing combo chart data for ngx-charts-combo-chart');
    // console.log('📊 AI Visibility: You university:', youUniversity?.name);
    // console.log('📈 AI Visibility: Competitor universities:', competitorUniversities.map(u => u.name));

    // Prepare bar chart data (results) - YOU university metrics as bars
    if (youUniversity) {
      this.results = [
        { name: 'AI Visibility Score', value: youUniversity.ai_visibility_score || 0 },
        { name: 'Content Quality', value: youUniversity.content_quality || 0 },
        { name: 'Digital Presence', value: youUniversity.digital_presence || 0 },
        { name: 'Innovation Index', value: youUniversity.innovation_index || 0 },
        { name: 'AI Adoption Rate', value: youUniversity.ai_adoption_rate || 0 }
      ];
    } else {
      this.results = [];
    }

    // Prepare line chart data (lineChart) - competitor universities as lines
    if (competitorUniversities.length > 0) {
      this.lineChart = competitorUniversities.map(university => ({
        name: university.name,
        series: [
          { name: 'AI Visibility Score', value: university.ai_visibility_score || 0 },
          { name: 'Content Quality', value: university.content_quality || 0 },
          { name: 'Digital Presence', value: university.digital_presence || 0 },
          { name: 'Innovation Index', value: university.innovation_index || 0 },
          { name: 'AI Adoption Rate', value: university.ai_adoption_rate || 0 }
        ]
      }));
    } else {
      this.lineChart = [];
    }

    // Set colors
    this.colors = this.comboBarScheme;

    // Set chart dimensions and transforms
    this.dims = {
      width: this.width,
      height: this.height,
      xAxisHeight: 50,
      yAxisWidth: 80,
      xAxisWidth: this.width - 80,
      yAxisHeight: this.height - 50
    };

    this.transform = `translate(${this.dims.yAxisWidth}, 0)`;

    // console.log('✅ AI Visibility: Combo chart data prepared:', {
    //   barResults: this.results.length,
    //   lineSeries: this.lineChart.length,
    //   youUniversity: youUniversity?.name,
    //   competitorCount: competitorUniversities.length
    // });
  }

  // Get all available metrics from a university object dynamically
  private getAvailableMetrics(university: any): any[] {
    const metrics = [];

    // Check for standard metrics
    if (university.ai_visibility_score !== undefined) {
      metrics.push({ name: 'AI Visibility Score', value: university.ai_visibility_score });
    }
    if (university.content_quality !== undefined) {
      metrics.push({ name: 'Content Quality', value: university.content_quality });
    }
    if (university.digital_presence !== undefined) {
      metrics.push({ name: 'Digital Presence', value: university.digital_presence });
    }
    if (university.innovation_index !== undefined) {
      metrics.push({ name: 'Innovation Index', value: university.innovation_index });
    }
    if (university.ai_adoption_rate !== undefined) {
      metrics.push({ name: 'AI Adoption Rate', value: university.ai_adoption_rate });
    }

    // Add any other numeric metrics
    Object.keys(university).forEach(key => {
      if (typeof university[key] === 'number' &&
          !['ai_visibility_score', 'content_quality', 'digital_presence', 'innovation_index', 'ai_adoption_rate'].includes(key)) {
        const metricName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        metrics.push({ name: metricName, value: university[key] });
      }
    });

    return metrics;
  }

  // Get metric value for a specific university and metric name - dynamic approach
  private getMetricValue(university: any, metricName: string): number {
    // Convert metric name back to property key
    const propertyKey = metricName.toLowerCase().replace(/\s+/g, '_');

    // Try direct property access first
    if (university[propertyKey] !== undefined) {
      return university[propertyKey];
    }

    // Try common variations
    const variations = [
      propertyKey,
      propertyKey.replace('_', ''),
      propertyKey.replace(/_/g, ''),
      metricName.toLowerCase().replace(/\s+/g, ''),
      metricName.toLowerCase().replace(/\s+/g, '_')
    ];

    for (const variation of variations) {
      if (university[variation] !== undefined) {
        return university[variation];
      }
    }

    // Fallback to 0 if not found
    console.warn(`AI Visibility: Metric "${metricName}" not found for university ${university.name}`);
    return 0;
  }

  // Check if a university is the "you" university
  isYouUniversity(university: any): boolean {
    if (!university) return false;

    // Check if university name contains "(YOU)" or "you"
    if (university.name && university.name.toLowerCase().includes('(you)')) {
      return true;
    }

    // Check if university name contains "you"
    if (university.name && university.name.toLowerCase().includes('you')) {
      return true;
    }

    // Check for is_your_brand property
    if (university.is_your_brand === true) {
      return true;
    }

    return false;
  }

  // Find university with "you" in the name or marked as your brand
  private findYouUniversity(universities: any[]): any | null {
    // If there's only one university, treat it as the "you" university
    if (universities.length === 1) {
      // console.log('🎯 AI Visibility: Single university detected, treating as "you" university:', universities[0].name);
      return universities[0];
    }

    // First, try to find university with "(YOU)" in the name
    let youUniversity = universities.find(university =>
      university.name && university.name.toLowerCase().includes('(you)')
    );

    // If not found, try to find university with "you" in the name
    if (!youUniversity) {
      youUniversity = universities.find(university =>
        university.name && university.name.toLowerCase().includes('you')
      );
    }

    // If still not found, check for is_your_brand property
    if (!youUniversity) {
      youUniversity = universities.find(university =>
        university.is_your_brand === true
      );
    }

    // If still not found, use the first university as fallback
    if (!youUniversity && universities.length > 0) {
      youUniversity = universities[0];
    }

    return youUniversity || null;
  }

  // Find competitor universities (those without "you" in name and not marked as your brand)
  private findCompetitorUniversities(universities: any[]): any[] {
    const youUniversity = this.findYouUniversity(universities);
    if (!youUniversity) return universities;

    return universities.filter(university =>
      university.name !== youUniversity.name
    );
  }

  // Get processed data for charts with caching
  getProcessedBarData() {
    if (this._cachedBarData.length === 0) {
      this._cachedBarData = this.barChartData;
    }
    return this._cachedBarData;
  }

  getProcessedLineData() {
    if (this._cachedLineData.length === 0) {
      this._cachedLineData = this.lineChartData;
    }
    return this._cachedLineData;
  }

  // Get the name of the first university (bar data) with caching
  getFirstUniversityName(): string {
    if (this._cachedFirstUniversityName === '' && this.aiVisibilityResponse?.universities) {
      const youUniversity = this.findYouUniversity(this.aiVisibilityResponse.universities);
      this._cachedFirstUniversityName = youUniversity ? youUniversity.name : '';
    }
    return this._cachedFirstUniversityName;
  }

  // Get line color for competitor universities
  getLineColor(universityName: string): string {
    const colors = ['#DB4437', '#F4B400', '#0F9D58', '#AB47BC'];
    const competitorUniversities = this.findCompetitorUniversities(this.aiVisibilityResponse?.universities || []);
    const index = competitorUniversities.findIndex(uni => uni.name === universityName);
    return colors[index % colors.length];
  }

  // Get custom colors for single university bar chart
  getCustomColors(): any[] {
    const colors = [
      { name: 'AI Visibility Score', value: '#1976d2' },
      { name: 'Content Quality', value: '#ff9800' },
      { name: 'Digital Presence', value: '#4caf50' },
      { name: 'Innovation Index', value: '#f44336' },
      { name: 'AI Adoption Rate', value: '#9c27b0' }
    ];

    // Map the colors to the actual data
    return this.barChartData.map((item, index) => ({
      name: item.name,
      value: colors[index % colors.length]?.value || '#1976d2'
    }));
  }

  private clearCache(): void {
    // console.log('🧹 AI Visibility: Clearing all cached data');
    this._cachedBarData = [];
    this._cachedLineData = [];
    this._cachedFirstUniversityName = '';

    // Also clear the main data arrays
    this.barChartData = [];
    this.lineChartData = [];
    // console.log('🧹 AI Visibility: All data arrays cleared');
  }

  createCitationBadge(citation: any): any {
    return {
      source_badge: `📊 ${citation.type || 'source'}`,
      live_badge: '🟢 Live',
      total_sources: 1,
      llm_sources: citation.type === 'llm' ? 1 : 0,
      bing_sources: citation.type === 'bing' ? 1 : 0,
      average_confidence: citation.confidence || 0.8
    };
  }

  transformCitationsToEvidenceSources(citations: any[]): any[] {
    return citations.map(citation => ({
      category: citation.category || 'ai-visibility',
      source: citation.source,
      url: citation.url,
      confidence: citation.confidence || 0.7,
      snippet: citation.snippet || '',
      type: citation.type || 'web'
    }));
  }
}
